import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Gear, CreditCard, Sparkle, UsersThree, Plus, CaretUp, CaretDown, X, CaretUpDown } from "@phosphor-icons/react";

const DESC_MAX = 280;

const RELATIONSHIP_OPTIONS = [
  "Mother",
  "Father",
  "Son",
  "Daughter",
  "Spouse",
  "Sibling",
];

// Roles that allow a second parent (blended-family support — Fix 4).
const DUAL_PARENT_ROLES = new Set(["Son", "Daughter"]);

const STATUS_BADGE = {
  trialing: { label: "Free trial", className: "bg-gold/20 text-forest border border-gold/40" },
  active: { label: "Active", className: "bg-forest/10 text-forest border border-forest/30" },
  past_due: { label: "Past due", className: "bg-red-100 text-red-700 border border-red-300" },
  paused: { label: "Paused", className: "bg-red-100 text-red-700 border border-red-300" },
  unpaid: { label: "Paused", className: "bg-red-100 text-red-700 border border-red-300" },
  canceled: { label: "Cancelled", className: "bg-cream text-sageGray border border-sage" },
  legacy: { label: "Legacy circle", className: "bg-cream text-sageGray border border-sage" },
};

export default function CircleSettingsDialog({ group, open, onOpenChange, onSaved, members = [] }) {
  const { user } = useAuth();
  const isOwner = group?.owner_id === user?.id;
  const [tab, setTab] = useState("general");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [structure, setStructure] = useState("flat");
  const [saving, setSaving] = useState(false);
  const [billing, setBilling] = useState(null);
  const [opening, setOpening] = useState(false);

  // Placeholders state
  const [placeholders, setPlaceholders] = useState([]);
  const [phForm, setPhForm] = useState({
    name: "",
    role_or_relationship: "",
    member_type: "family",
    branch: "",
    // "Connected to" stores both kind + id so we can route to the right backend field.
    connected_kind: "",   // "" | "member" | "placeholder"
    connected_id: "",     // user_id or placeholder_id
    // Second "Connected to" anchor — only shown for Son/Daughter (Fix 4).
    connected2_kind: "",
    connected2_id: "",
  });
  const [phSaving, setPhSaving] = useState(false);

  const loadPlaceholders = useCallback(async () => {
    if (!group?.id) return;
    try {
      const { data } = await api.get(`/groups/${group.id}/placeholders`, { params: { include_claimed: true } });
      setPlaceholders(data);
    } catch {
      // pending members get 403 — ignore
    }
  }, [group?.id]);

  useEffect(() => {
    if (!open || !group) return;
    setTab("general");
    setName(group.name || "");
    setDescription(group.description || "");
    setStructure(group.structure || "flat");
    if (isOwner) {
      api.get(`/billing/status/${group.id}`).then(r => setBilling(r.data)).catch(() => setBilling(null));
      loadPlaceholders();
    } else {
      setBilling(null);
      setPlaceholders([]);
    }
  }, [open, group, isOwner, loadPlaceholders]);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Circle name can't be empty");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/groups/${group.id}/settings`, {
        name: name.trim(),
        description: description.trim(),
        structure,
      });
      toast.success("Circle settings saved");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't save settings");
    } finally {
      setSaving(false);
    }
  };

  const openPortal = async () => {
    setOpening(true);
    try {
      const { data } = await api.post("/billing/portal", { group_id: group.id, origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't open billing portal");
      setOpening(false);
    }
  };

  const addPlaceholder = async (e) => {
    e.preventDefault();
    if (!phForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    // Fix 9: "Connected to" is required for family placeholders.
    if (phForm.member_type === "family") {
      if (!phForm.role_or_relationship) {
        toast.error("Please choose a relationship");
        return;
      }
      if (!phForm.connected_id) {
        toast.error("Select who this person is connected to so they appear in the correct place on your tree.");
        return;
      }
    }
    setPhSaving(true);
    try {
      // Resolve the "Connected to" selection into the right backend field.
      const parent_user_id = phForm.connected_kind === "member" ? phForm.connected_id || null : null;
      const parent_placeholder_id = phForm.connected_kind === "placeholder" ? phForm.connected_id || null : null;
      // Second-parent fields (Fix 4): only meaningful for Son/Daughter.
      const isDual = DUAL_PARENT_ROLES.has(phForm.role_or_relationship);
      const parent2_user_id = isDual && phForm.connected2_kind === "member" ? phForm.connected2_id || null : null;
      const parent2_placeholder_id = isDual && phForm.connected2_kind === "placeholder" ? phForm.connected2_id || null : null;
      // Position at end of current siblings under that parent.
      const siblings = placeholders.filter((p) =>
        (p.parent_user_id || "") === (parent_user_id || "") &&
        (p.parent_placeholder_id || "") === (parent_placeholder_id || "")
      );
      const position_order = siblings.length;
      await api.post(`/groups/${group.id}/placeholders`, {
        name: phForm.name.trim(),
        role_or_relationship: phForm.role_or_relationship,
        member_type: phForm.member_type,
        branch: phForm.branch || null,
        position_order,
        parent_user_id,
        parent_placeholder_id,
        parent2_user_id,
        parent2_placeholder_id,
      });
      toast.success("Placeholder added");
      setPhForm({
        name: "",
        role_or_relationship: "",
        member_type: phForm.member_type,
        branch: "",
        connected_kind: "",
        connected_id: "",
        connected2_kind: "",
        connected2_id: "",
      });
      await loadPlaceholders();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't add placeholder");
    } finally {
      setPhSaving(false);
    }
  };

  const deletePlaceholder = async (p) => {
    if (!window.confirm(`Delete placeholder "${p.name}"? Any relationships routed through it will also be removed.`)) return;
    try {
      await api.delete(`/groups/${group.id}/placeholders/${p.id}`);
      toast.success("Placeholder removed");
      await loadPlaceholders();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't delete placeholder");
    }
  };

  const reorderPlaceholder = async (p, delta) => {
    // Swap position_order with the neighbour in the same parent group.
    const siblings = placeholders
      .filter((x) => (x.parent_user_id || "") === (p.parent_user_id || ""))
      .sort((a, b) => (a.position_order || 0) - (b.position_order || 0));
    const idx = siblings.findIndex((x) => x.id === p.id);
    const swap = siblings[idx + delta];
    if (!swap) return;
    try {
      await Promise.all([
        api.patch(`/groups/${group.id}/placeholders/${p.id}/reorder`, { position_order: swap.position_order || 0 }),
        api.patch(`/groups/${group.id}/placeholders/${swap.id}/reorder`, { position_order: p.position_order || 0 }),
      ]);
      await loadPlaceholders();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't reorder");
    }
  };

  const statusKey = billing?.status || "legacy";
  const statusVisual = STATUS_BADGE[statusKey] || STATUS_BADGE.legacy;
  const trialEnd = billing?.trial_end_at ? new Date(billing.trial_end_at) : null;
  const renews = billing?.current_period_end_at ? new Date(billing.current_period_end_at) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="circle-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-forest">Circle settings</DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab} className="pt-1">
          <TabsList className="bg-cream border border-sage rounded-full p-1 mb-5 w-fit flex-wrap">
            <TabsTrigger value="general" className="data-[state=active]:bg-forest data-[state=active]:text-ivory rounded-full px-4 py-1.5 text-xs uppercase tracking-widest" data-testid="settings-tab-general">
              <Gear size={14} className="mr-1.5" /> General
            </TabsTrigger>
            {isOwner && (
              <TabsTrigger value="placeholders" className="data-[state=active]:bg-forest data-[state=active]:text-ivory rounded-full px-4 py-1.5 text-xs uppercase tracking-widest" data-testid="settings-tab-placeholders">
                <UsersThree size={14} className="mr-1.5" /> Placeholders
              </TabsTrigger>
            )}
            {isOwner && (
              <TabsTrigger value="billing" className="data-[state=active]:bg-forest data-[state=active]:text-ivory rounded-full px-4 py-1.5 text-xs uppercase tracking-widest" data-testid="settings-tab-billing">
                <CreditCard size={14} className="mr-1.5" /> Billing
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="general">
            <form onSubmit={save} className="space-y-5" data-testid="circle-general-form">
              <div>
                <Label className="text-forest">Circle name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="circle-name-input" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-forest">Description</Label>
                  <span className="text-[11px] text-sageGray">{description.length}/{DESC_MAX}</span>
                </div>
                <Textarea rows={4} value={description} maxLength={DESC_MAX} onChange={(e) => setDescription(e.target.value)} placeholder="A short note about this circle." className="mt-1.5 bg-white border-sage focus-visible:ring-gold" data-testid="circle-description-input" />
              </div>
              {isOwner && (
                <div data-testid="circle-structure-block">
                  <Label className="text-forest">Structure</Label>
                  <p className="text-[11px] text-sageGray mb-2">Choose how the connection view is rendered for this circle.</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setStructure("flat")}
                      className={`flex-1 rounded-lg border-2 px-4 py-3 text-left transition-colors ${structure === "flat" ? "border-forest bg-forest/5" : "border-sage hover:border-forest/40"}`}
                      data-testid="circle-structure-flat"
                    >
                      <div className="font-display text-forest">Flat / Family</div>
                      <div className="text-[11px] text-sageGray mt-0.5">Connection map + family tree.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setStructure("hierarchy")}
                      className={`flex-1 rounded-lg border-2 px-4 py-3 text-left transition-colors ${structure === "hierarchy" ? "border-forest bg-forest/5" : "border-sage hover:border-forest/40"}`}
                      data-testid="circle-structure-hierarchy"
                    >
                      <div className="font-display text-forest">Hierarchy / Org</div>
                      <div className="text-[11px] text-sageGray mt-0.5">Org chart with roles (Pastor → Elders → Members).</div>
                    </button>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-sageGray hover:bg-cream rounded-full" data-testid="circle-settings-cancel">Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-forest hover:bg-forestSoft text-ivory rounded-full px-6" data-testid="circle-settings-save">{saving ? "Saving…" : "Save changes"}</Button>
              </div>
            </form>
          </TabsContent>

          {isOwner && (
            <TabsContent value="placeholders" data-testid="placeholders-tab-content">
              <PlaceholdersPanel
                phForm={phForm}
                setPhForm={setPhForm}
                phSaving={phSaving}
                addPlaceholder={addPlaceholder}
                placeholders={placeholders}
                deletePlaceholder={deletePlaceholder}
                reorderPlaceholder={reorderPlaceholder}
                structure={structure}
                members={members}
              />
            </TabsContent>
          )}

          {isOwner && (
            <TabsContent value="billing" data-testid="billing-tab-content">
              {!billing ? (
                <p className="text-sm text-sageGray">Loading billing info…</p>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-xl border-2 border-sage bg-cream/40 p-5">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <p className="text-[11px] uppercase tracking-widest text-sageGray">Current plan</p>
                        <p className="font-display text-2xl text-forest capitalize" data-testid="billing-plan-name">
                          {billing.plan || "—"} <span className="text-base text-sageGray font-sans">· {billing.interval || ""}</span>
                        </p>
                      </div>
                      <Badge className={`${statusVisual.className} rounded-full text-[10px] uppercase tracking-widest`} data-testid="billing-status-badge">
                        {statusKey === "trialing" && <Sparkle size={11} weight="fill" className="mr-1" />}
                        {statusVisual.label}
                      </Badge>
                    </div>
                    {trialEnd && statusKey === "trialing" && (
                      <p className="text-sm text-sageGray mt-3" data-testid="billing-trial-end">
                        Free until <strong>{trialEnd.toLocaleDateString()}</strong>. Your card will be charged after that unless you cancel.
                      </p>
                    )}
                    {renews && statusKey === "active" && (
                      <p className="text-sm text-sageGray mt-3" data-testid="billing-renew-date">
                        Renews on <strong>{renews.toLocaleDateString()}</strong>.
                      </p>
                    )}
                    {(statusKey === "paused" || statusKey === "past_due" || statusKey === "unpaid") && (
                      <p className="text-sm text-red-700 mt-3" data-testid="billing-paused-notice">
                        Payment couldn't be collected. Members can still view this circle but can't invite new members or send messages until billing is resolved.
                      </p>
                    )}
                    {statusKey === "canceled" && (
                      <p className="text-sm text-sageGray mt-3">This circle's subscription has been cancelled.</p>
                    )}
                    {statusKey === "legacy" && (
                      <p className="text-sm text-sageGray mt-3">This circle was created before billing was introduced. It will continue to work without a subscription.</p>
                    )}
                  </div>

                  {statusKey !== "legacy" && (
                    <Button
                      onClick={openPortal}
                      disabled={opening}
                      className="w-full bg-forest hover:bg-forestSoft text-ivory rounded-full h-11"
                      data-testid="billing-portal-button"
                    >
                      {opening ? "Opening…" : "Manage subscription · Upgrade · Cancel"}
                    </Button>
                  )}
                  <p className="text-[11px] text-sageGray text-center">
                    Powered by Stripe. We never store your card details.
                  </p>
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function PlaceholdersPanel({ phForm, setPhForm, phSaving, addPlaceholder, placeholders, deletePlaceholder, reorderPlaceholder, structure, members }) {
  // Group by parent for the visual ordering. Includes parent_placeholder_id
  // (chained placeholders) so an "Under Sarah (placeholder)" heading appears
  // when a placeholder is linked to another placeholder.
  const groupedByParent = new Map();
  for (const p of placeholders) {
    const k = p.parent_user_id || (p.parent_placeholder_id ? `ph:${p.parent_placeholder_id}` : "__root__");
    if (!groupedByParent.has(k)) groupedByParent.set(k, []);
    groupedByParent.get(k).push(p);
  }
  for (const list of groupedByParent.values()) {
    list.sort((a, b) => (a.position_order || 0) - (b.position_order || 0));
  }
  const isOrg = structure === "hierarchy";

  return (
    <div className="space-y-6">
      <form onSubmit={addPlaceholder} className="rounded-xl border-2 border-sage bg-cream/30 p-4 space-y-3" data-testid="placeholder-add-form">
        <div className="flex items-center gap-2">
          <Plus size={16} weight="bold" className="text-forest" />
          <p className="font-display text-forest">Add a placeholder seat</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-forest text-xs">Name</Label>
            <Input
              required
              value={phForm.name}
              onChange={(e) => setPhForm({ ...phForm, name: e.target.value })}
              className="mt-1 bg-white border-sage focus-visible:ring-gold"
              data-testid="placeholder-name-input"
            />
          </div>
          <div>
            <Label className="text-forest text-xs">{isOrg ? "Role" : "Relationship"}</Label>
            {isOrg ? (
              <Input
                value={phForm.role_or_relationship}
                onChange={(e) => setPhForm({ ...phForm, role_or_relationship: e.target.value })}
                placeholder="e.g. Pastor, Elder"
                className="mt-1 bg-white border-sage focus-visible:ring-gold"
                data-testid="placeholder-role-input"
              />
            ) : (
              <Select
                value={phForm.role_or_relationship}
                onValueChange={(v) => setPhForm({ ...phForm, role_or_relationship: v })}
              >
                <SelectTrigger className="mt-1 bg-white border-sage" data-testid="placeholder-relationship-select">
                  <SelectValue placeholder="Choose a relationship" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {RELATIONSHIP_OPTIONS.map((rel) => (
                    <SelectItem key={rel} value={rel} data-testid={`placeholder-relationship-option-${rel}`}>
                      {rel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="text-forest text-xs">Type</Label>
            <Select value={phForm.member_type} onValueChange={(v) => setPhForm({ ...phForm, member_type: v })}>
              <SelectTrigger className="mt-1 bg-white border-sage" data-testid="placeholder-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="family">Family member</SelectItem>
                <SelectItem value="org_role">Org / Role seat</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {phForm.member_type === "family" && (
            <div>
              <Label className="text-forest text-xs">Branch (optional)</Label>
              <Select value={phForm.branch} onValueChange={(v) => setPhForm({ ...phForm, branch: v })}>
                <SelectTrigger className="mt-1 bg-white border-sage" data-testid="placeholder-branch-select">
                  <SelectValue placeholder="Choose a side" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paternal">Paternal side</SelectItem>
                  <SelectItem value="maternal">Maternal side</SelectItem>
                  <SelectItem value="both">Both / not sure</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className={phForm.member_type === "family" ? "" : "sm:col-span-2"}>
            <Label className="text-forest text-xs">
              {(() => {
                if (phForm.member_type !== "family") return "Connected to (optional)";
                if (DUAL_PARENT_ROLES.has(phForm.role_or_relationship)) return "Parent 1 (required)";
                return "Connected to (required)";
              })()}
            </Label>
            <ConnectedToCombobox
              members={members}
              placeholders={placeholders}
              kind={phForm.connected_kind}
              id={phForm.connected_id}
              onSelect={(kind, id) => setPhForm({ ...phForm, connected_kind: kind, connected_id: id })}
            />
            <p className="text-[10px] text-sageGray mt-1">
              {phForm.member_type === "family"
                ? `Pick the ${DUAL_PARENT_ROLES.has(phForm.role_or_relationship) ? "first parent" : "person"} this placeholder is related to.`
                : `Pick the member or placeholder this seat sits next to on the ${isOrg ? "chart" : "tree"}.`}
            </p>
          </div>
          {phForm.member_type === "family" && DUAL_PARENT_ROLES.has(phForm.role_or_relationship) && (
            <div data-testid="placeholder-second-parent-block">
              <Label className="text-forest text-xs">Parent 2 (optional)</Label>
              <ConnectedToCombobox
                members={members}
                placeholders={placeholders}
                kind={phForm.connected2_kind}
                id={phForm.connected2_id}
                onSelect={(kind, id) => setPhForm({ ...phForm, connected2_kind: kind, connected2_id: id })}
              />
              <p className="text-[10px] text-sageGray mt-1">
                Use this when the child has two parents on the tree (blended families).
              </p>
            </div>
          )}
        </div>
        <Button
          type="submit"
          disabled={phSaving}
          className="bg-forest hover:bg-forestSoft text-ivory rounded-full"
          data-testid="placeholder-add-submit"
        >
          {phSaving ? "Adding…" : "Add placeholder"}
        </Button>
      </form>

      {placeholders.length === 0 ? (
        <div className="text-center py-8 text-sm text-sageGray" data-testid="placeholders-empty">
          No placeholders yet. Add one above and a clickable open-seat node will appear on the {isOrg ? "org chart" : "tree"}.
        </div>
      ) : (
        <div className="space-y-4">
          {Array.from(groupedByParent.entries()).map(([parentKey, list]) => {
            let parentLabel = "Top tier";
            if (parentKey !== "__root__") {
              if (parentKey.startsWith("ph:")) {
                const ph = placeholders.find((x) => x.id === parentKey.slice(3));
                parentLabel = ph ? `Under ${ph.name} (placeholder)` : "Under placeholder";
              } else {
                const m = members.find((x) => (x.user_id || x.id) === parentKey);
                parentLabel = m ? `Under ${m.name}` : "Under member";
              }
            }
            return (
              <div key={parentKey}>
                <p className="text-[10px] uppercase tracking-widest text-sageGray mb-1.5">
                  {parentLabel}
                </p>
                <ul className="space-y-2">
                  {list.map((p, idx) => (
                    <li
                      key={p.id}
                      className="flex items-center gap-3 rounded-lg border border-sage bg-white px-3 py-2"
                      data-testid={`placeholder-row-${p.id}`}
                    >
                      <div className="w-9 h-9 rounded-full bg-cream border-2 border-dashed border-[#E89C3F] flex items-center justify-center overflow-hidden shrink-0">
                        {p.photo_path ? (
                          <img src={fileUrl(p.photo_path)} alt={p.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-sm font-display text-sageGray">{(p.name || "?").charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-charcoal truncate">{p.name}</p>
                        <p className="text-[11px] uppercase tracking-widest text-sageGray">
                          {p.role_or_relationship || (p.member_type === "org_role" ? "Role" : "Family")}
                          {p.branch ? ` · ${p.branch}` : ""}
                        </p>
                        {(() => {
                          const linkedMember = p.parent_user_id ? members.find((m) => (m.user_id || m.id) === p.parent_user_id) : null;
                          const linkedPh = p.parent_placeholder_id ? placeholders.find((x) => x.id === p.parent_placeholder_id) : null;
                          const linkedLabel = linkedMember?.name || (linkedPh ? `${linkedPh.name} (placeholder)` : null);
                          if (!linkedLabel) return null;
                          return (
                            <p className="text-[11px] text-sageGray mt-0.5 truncate" data-testid={`placeholder-connected-to-${p.id}`}>
                              <span className="text-forest/70">Connected to:</span> {linkedLabel}
                            </p>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => reorderPlaceholder(p, -1)}
                          disabled={idx === 0}
                          className="w-7 h-7 rounded-full border border-sage text-forest disabled:opacity-40 hover:bg-cream flex items-center justify-center"
                          data-testid={`placeholder-up-${p.id}`}
                          title="Move up"
                        >
                          <CaretUp size={12} weight="bold" />
                        </button>
                        <button
                          onClick={() => reorderPlaceholder(p, 1)}
                          disabled={idx === list.length - 1}
                          className="w-7 h-7 rounded-full border border-sage text-forest disabled:opacity-40 hover:bg-cream flex items-center justify-center"
                          data-testid={`placeholder-down-${p.id}`}
                          title="Move down"
                        >
                          <CaretDown size={12} weight="bold" />
                        </button>
                        <button
                          onClick={() => deletePlaceholder(p)}
                          className="w-7 h-7 rounded-full border border-sage text-red-600 hover:bg-red-50 flex items-center justify-center"
                          data-testid={`placeholder-delete-${p.id}`}
                          title="Delete"
                        >
                          <X size={12} weight="bold" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/**
 * Searchable combobox listing both verified members and existing placeholders.
 * Selecting a row calls `onSelect(kind, id)` where kind is "member" or
 * "placeholder". Clearing returns ("", "") which writes neither
 * parent_user_id nor parent_placeholder_id on submit.
 */
function ConnectedToCombobox({ members, placeholders, kind, id, onSelect }) {
  const [open, setOpen] = useState(false);
  const verifiedMembers = (members || []).filter((m) => m.status === "verified");
  const memberById = new Map(verifiedMembers.map((m) => [m.user_id || m.id, m]));
  const placeholderById = new Map((placeholders || []).map((p) => [p.id, p]));
  const selectedLabel = (() => {
    if (kind === "member") {
      const m = memberById.get(id);
      return m ? m.name : "";
    }
    if (kind === "placeholder") {
      const p = placeholderById.get(id);
      return p ? `${p.name} (placeholder)` : "";
    }
    return "";
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="mt-1 w-full inline-flex items-center justify-between rounded-md border border-sage bg-white px-3 py-2 text-sm text-left text-forest hover:bg-cream focus:outline-none focus:ring-2 focus:ring-gold/40"
          data-testid="placeholder-connected-trigger"
        >
          <span className={selectedLabel ? "" : "text-sageGray"}>
            {selectedLabel || "No connection (top of tree)"}
          </span>
          <CaretUpDown size={14} className="ml-2 opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] bg-white" align="start">
        <Command>
          <CommandInput placeholder="Search members and placeholders…" data-testid="placeholder-connected-search" />
          <CommandList>
            <CommandEmpty>Nothing matches.</CommandEmpty>
            <CommandItem
              value="__clear__"
              onSelect={() => { onSelect("", ""); setOpen(false); }}
              data-testid="placeholder-connected-clear"
              className="text-sageGray italic"
            >
              No connection (top of tree)
            </CommandItem>
            {verifiedMembers.length > 0 && (
              <CommandGroup heading="Members">
                {verifiedMembers.map((m) => {
                  const value = m.user_id || m.id;
                  return (
                    <CommandItem
                      key={`m-${value}`}
                      value={`member ${m.name}`}
                      onSelect={() => { onSelect("member", value); setOpen(false); }}
                      data-testid={`placeholder-connected-member-${value}`}
                    >
                      {m.name}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {placeholders && placeholders.length > 0 && (
              <CommandGroup heading="Placeholders">
                {placeholders.map((p) => (
                  <CommandItem
                    key={`p-${p.id}`}
                    value={`placeholder ${p.name} ${p.role_or_relationship || ""}`}
                    onSelect={() => { onSelect("placeholder", p.id); setOpen(false); }}
                    data-testid={`placeholder-connected-placeholder-${p.id}`}
                  >
                    <span className="flex-1">{p.name}</span>
                    {p.role_or_relationship && (
                      <span className="text-[11px] uppercase tracking-widest text-sageGray ml-2">
                        {p.role_or_relationship}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
